import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BookingLinkKind = "private" | "nhs" | "vaccine" | "page";

type BookingMap = Record<string, string>;

let cache: BookingMap | null = null;
let inFlight: Promise<BookingMap> | null = null;
const subscribers = new Set<(m: BookingMap) => void>();

const keyOf = (kind: BookingLinkKind, id: string) => `${kind}::${id}`;

async function loadBookingMap(): Promise<BookingMap> {
  if (cache) return cache;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { data, error } = await supabase
      .from("service_booking_links")
      .select("kind, service_id, booking_url");
    if (error) {
      console.error("[booking-links] load failed", error);
      return {};
    }
    const map: BookingMap = {};
    for (const row of data ?? []) {
      map[keyOf(row.kind as BookingLinkKind, row.service_id)] = row.booking_url;
    }
    cache = map;
    subscribers.forEach((cb) => cb(map));
    return map;
  })();

  return inFlight;
}

/**
 * Returns the Setmore booking URL for a service, or `fallback` while loading
 * or if the mapping does not exist. Falls back to the central Setmore landing
 * page if no fallback is provided.
 */
export function useBookingLink(
  kind: BookingLinkKind,
  serviceId: string,
  fallback = "https://healthclinicshull.setmore.com/",
): string {
  const [url, setUrl] = useState<string>(() => {
    if (cache) return cache[keyOf(kind, serviceId)] ?? fallback;
    return fallback;
  });

  useEffect(() => {
    let active = true;
    const apply = (map: BookingMap) => {
      if (!active) return;
      setUrl(map[keyOf(kind, serviceId)] ?? fallback);
    };
    subscribers.add(apply);
    loadBookingMap().then(apply);
    return () => {
      active = false;
      subscribers.delete(apply);
    };
  }, [kind, serviceId, fallback]);

  return url;
}

/** Force a refetch (used by the admin page after edits). */
export function invalidateBookingLinks() {
  cache = null;
  inFlight = null;
  loadBookingMap();
}
